/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import { useNavigate } from "react-router-dom";

import { useFormik } from "formik";
import * as Yup from "yup";
import { faUserCircle } from "@fortawesome/free-solid-svg-icons";
import {
  FC,
  useEffect,
  useState,
  SetStateAction,
  useMemo,
  useRef,
} from "react";
import {
  ProfileDetails,
  UpdateProfile,
  addressUserFetch,
  callGooglePlacesApi,
  locationLatLongAPIFetch,
} from "../core/_requests";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { Modal } from "react-bootstrap";
import { ChefProfile, useAuth } from "../../../auth";
import { chefProfileSchema } from "../core/Schema";
import ListSettings from "./ListSettings";
// import locationIcon from "/assets/images/locationicon.svg";
// import locationIcon from "../../../../../public//assets/images/locationicon.svg";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
};

const libraries = ["places"];

type Props = {
  initialValues: any;
};

const ProfileUpdate: FC<Props> = ({ initialValues }) => {
  const [data, setData] = useState<ChefProfile>(initialValues);
  const [showMarker, setShowMarker] = useState(false);
  const LatLng = {
    lat: Number(data.latitude),
    lng: Number(data.longitude),
  };
 const center = useMemo(
  () => ({ lat: Number(data.latitude), lng: Number(data.longitude) }),
  [data.latitude, data.longitude] // <-- update when data comes from API
);
;

  const { currentUser, setCurrentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showFileMessage, setShowFileMessage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [formImage, setFormImage] = useState<File | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [mapCenter, setMapCenter] = useState(center);
  const [userLocation, setUserLocation] = useState(LatLng);
  const [addressData, setAddressData] = useState<any>({});
  const [suggestions, setSuggestions] = useState<any>([]);
  const [googledata, setgoogledata] = useState<any>({});
  const [dropdownCLicked, setdropdownCLicked] = useState(false);
  const inputs = useRef<Array<HTMLInputElement | HTMLSelectElement | null>>([]);
  const chefId = currentUser?.uuid;
  const googleMapsApiKey = process.env.REACT_APP_PLACES_API_KEY || "";
  const [icon, setIcon] = useState<any>("");

  const clickCancel = () => {
    navigate("/settings");
  };

  useEffect(() => {
    ProfileDetails(chefId)
      .then((res) => {
        setData(res?.data);
        setImagePreview(res?.data?.profile);
        const latitude = res?.data?.latitude;
        const longitude = res?.data?.longitude;

        if (latitude && longitude) {
          setUserLocation({
            lat: latitude,
            lng: longitude,
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
      });
  }, []);

  const formik = useFormik<ChefProfile>({
    initialValues,
    validationSchema: chefProfileSchema,
    onSubmit: async (values: any, actions: any) => {
      setLoading(true);
      const updatedData = Object.assign(data, values);

      UpdateProfile(updatedData, chefId).then((res) => {
        if (res?.status === true) {
          setCurrentUser((prevUser) => {
            if (!prevUser) return undefined; // Ensure prevUser is defined before updating

            const updatedUser = {
              ...prevUser,
              address: {
                ...prevUser.address,
                address: formik.values.address, // ✅ Updating address
              },
            };

            // Ensure localStorage is in sync with state
            localStorage.setItem(
              "frayoChefPortal",
              JSON.stringify(updatedUser)
            );

            return updatedUser;
          });

          navigate("/settings/profile-success", { replace: true });
          setLoading(false);
        } else {
          setLoading(false);
        }
      });
    },
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey,
    // libraries,
  });
  //

  useEffect(() => {
    const checkGoogle = setInterval(() => {
      if (window.google && window.google.maps) {
        setIcon({
          url: "/assets/images/locationicon.svg",
          scaledSize: new google.maps.Size(40, 40), // ✅ Corrected
        });
        clearInterval(checkGoogle);
      }
    }, 300); // Retry every 300ms
    return () => clearInterval(checkGoogle);
  }, []);

  const openMap = () => {
    setShowModal(true);
  };

  const handleMapClick = (event: any) => {
    setMapCenter({
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    });
    setShowMarker(true);
   
  };

  const closeMap = () => {
    setShowModal(false);
  };

  const handleMarkerDragEnd = (event: any) => {
    const newLat = event.latLng.lat();
    const newLng = event.latLng.lng();
    setUserLocation({ lat: newLat, lng: newLng });
    setMapCenter({
      lat: newLat,
      lng: newLng,
    });
    
  };

  const fetchAddress = async () => {
    const data = mapCenter;
    await addressUserFetch(data).then((res: any) => {
      formik.setFieldValue("address", res?.address);
      formik.setFieldValue("city", res?.city);
      formik.setFieldValue("state", res?.state);
      formik.setFieldValue("country", res?.country);
formik.setFieldValue("timeZone", res?.timeZone || "");

      formik.setFieldValue("zipCode", res?.pincode);
      setAddressData(res);
    });
    setShowModal(false);
  };

  const handleGoogleApi = (Inputvalue: any) => {
    formik.setFieldValue("address", Inputvalue);
    if (Inputvalue.length >= 5) {
      callGooglePlacesApi(Inputvalue).then((res: SetStateAction<never[]>) => {
        setSuggestions(res);
      });
    }
  };

  const handleCitySelection = (placeId: any, description: any) => {
    setdropdownCLicked(true);
    formik.setFieldValue("address", description);
    locationLatLongAPIFetch(placeId).then((res: any) => {
      setgoogledata(res);
      formik.setFieldValue("address", res?.address);
      formik.setFieldValue("city", res?.cityName);
      formik.setFieldValue("state", res?.stateName);
      formik.setFieldValue("country", res?.countryName);
      formik.setFieldValue("latitude", res?.latitude);
      formik.setFieldValue("longitude", res?.longitude);
      formik.setFieldValue("zipCode", res?.pincode);
      formik.setFieldValue("timeZone", res?.timeZone);
    });
    setSuggestions([]); // Clear suggestions after selection
  };

  const handleUseCurrentLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          const latLng = { lat: latitude, lng: longitude };
          setUserLocation(latLng);
          setMapCenter(latLng);
          setShowMarker(true);
          setShowModal(true); // Show map modal

          // 🧠 Get address from lat/lng
          const res = await addressUserFetch(latLng);
          
          if (res) {
            formik.setFieldValue("address", res?.address || "");
            formik.setFieldValue("city", res?.city || "");
            formik.setFieldValue("state", res?.state || "");
            formik.setFieldValue("country", res?.country || "");
            formik.setFieldValue("zipCode", res?.pincode || "");
            formik.setFieldValue("latitude", latitude);
            formik.setFieldValue("longitude", longitude);
            formik.setFieldValue("timezone", res?.timeZone || "");
            setAddressData(res);
          }
        },
        (error) => {
          console.error("📵 Failed to get current location:", error);
          alert(
            "Unable to get your current location. Please check location permissions."
          );
        },
        { enableHighAccuracy: true }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  // Ask for location on mount (or on demand)
  useEffect(() => {
  if ("geolocation" in navigator) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        const latLng = { lat: latitude, lng: longitude };

        // Update BOTH states so the map moves!
        setUserLocation(latLng);
        setMapCenter(latLng);
      },
      (error) => console.log(error),
      { enableHighAccuracy: true }
    );
  }
}, []);


  return (
    <>
      <section className="container-fluid">
        <ListSettings />
        <div className="row">
          <section className="content-section">
            <div className="overlay"></div>
            <div className="drawer small">
              <div className="title row">
                <h3 className="col-10 mb-0">Edit Profile Details</h3>
                <div className="col-2 text-end">
                  <button
                    className="btn p-0 close d-inline-flex"
                    onClick={clickCancel}
                  >
                    <i className="material-symbols-outlined">close</i>
                  </button>
                </div>
              </div>

              <form
                onSubmit={formik.handleSubmit}
                noValidate
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
              >
                <div className="col-12 mb-3 mt-3">
                  <label htmlFor="" className="form-label">
                    Your Name
                  </label>
                  <input
                    type="text"
                    className={clsx(
                      "form-control",
                      {
                        "is-invalid": formik.touched.name && formik.errors.name,
                      },
                      {
                        "is-valid": formik.touched.name && !formik.errors.name,
                      }
                    )}
                    placeholder="Give your name"
                    {...formik.getFieldProps(`name`)}
                    ref={(el) => {
                      inputs.current[1] = el;
                    }}
                    onKeyPress={(e) =>
                      !/^[a-zA-Z0-9\s]*$/.test(e.key) && e.preventDefault()
                    }
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="fv-plugins-message-container">
                      <span role="alert">{formik.errors.name}</span>
                    </div>
                  )}
                </div>

                <div className="col-12 mb-3 mt-3">
                  <label htmlFor="" className="form-label">
                    Your Email
                  </label>
                  <input
                    type="email"
                    className={clsx(
                      "form-control",
                      {
                        "is-invalid":
                          formik.touched.email && formik.errors.email,
                      },
                      {
                        "is-valid":
                          formik.touched.email && !formik.errors.email,
                      }
                    )}
                    {...formik.getFieldProps(`email`)}
                    ref={(el) => {
                      inputs.current[1] = el;
                    }}
                    placeholder="Give your email"
                    readOnly={true}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <div className="fv-plugins-message-container">
                      <span role="alert">{formik.errors.email}</span>
                    </div>
                  )}
                </div>

                <div className="col-12 mb-3 mt-3">
                  <label htmlFor="" className="form-label">
                    Your Mobile Number
                  </label>
                  <div className="input-group">
                    <div className="input-group-text">
                      <img src="/assets/images/united-states.png" alt="flag" />{" "}
                      +1
                    </div>
                    <input
                      type="text"
                      className={clsx(
                        "form-control",
                        {
                          "is-invalid":
                            formik.touched.mobileNumber &&
                            formik.errors.mobileNumber,
                        },
                        {
                          "is-valid":
                            formik.touched.mobileNumber &&
                            !formik.errors.mobileNumber,
                        }
                      )}
                      {...formik.getFieldProps("mobileNumber")} // ✅ This already includes value and onChange
                      ref={(el) => {
                        inputs.current[1] = el;
                      }}
                      placeholder="Give your mobile number"
                      maxLength={10}
                      onKeyPress={(e) =>
                        !/[0-9]/.test(e.key) && e.preventDefault()
                      }
                    />
                  </div>
                  {formik.touched.mobileNumber &&
                    formik.errors.mobileNumber && (
                      <div className="fv-plugins-message-container">
                        <span role="alert">{formik.errors.mobileNumber}</span>
                      </div>
                    )}
                </div>

                <div className="col-12 mb-3 mt-3">
                  <label htmlFor="" className="form-label">
                    Your Mobile Number (optional)
                  </label>
                  <div className="input-group">
                    <div className="input-group-text">
                      <img src="/assets/images/united-states.png" alt="flag" />{" "}
                      +1
                    </div>
                    <input
                      type="text"
                      className={clsx(
                        "form-control",
                        {
                          "is-invalid":
                            formik.touched.mobileNumber1 &&
                            formik.errors.mobileNumber1,
                        },
                        {
                          "is-valid":
                            formik.touched.mobileNumber1 &&
                            !formik.errors.mobileNumber1,
                        }
                      )}
                      {...formik.getFieldProps("mobileNumber1")} // ✅ This already includes value and onChange
                      ref={(el) => {
                        inputs.current[1] = el;
                      }}
                      placeholder="Give your mobile number"
                      maxLength={10}
                      onKeyPress={(e) =>
                        !/[0-9]/.test(e.key) && e.preventDefault()
                      }
                    />
                  </div>
                  {formik.touched.mobileNumber1 &&
                    formik.errors.mobileNumber1 && (
                      <div className="fv-plugins-message-container">
                        <span role="alert">{formik.errors.mobileNumber1}</span>
                      </div>
                    )}
                </div>
                <div className="col-12">
                  <label
                    htmlFor="exampleFormControlInput1"
                    className="small-font mb-2"
                  >
                    Your Address (Select your address)
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className={clsx(
                        "form-control",
                        {
                          "is-invalid":
                            formik.touched.address && formik.errors.address,
                        },
                        {
                          "is-valid":
                            formik.touched.address && !formik.errors.address,
                        }
                      )}
                      ref={(el) => {
                        inputs.current[1] = el;
                      }}
                      placeholder="Door no., Street, Area..."
                      {...formik.getFieldProps("address")}
                      onChange={(e) => handleGoogleApi(e.target.value)}
                    />
                    <div className="input-group-append">
                      <img
                        onClick={openMap}
                        src="/assets/images/map.png"
                        className="map-icon mt-2 mx-3"
                        alt="Map Icon"
                        style={{ width: "20px", height: "25px" }}
                      />
                    </div>
                  </div>
                  {suggestions.length > 0 ? (
                    <ul className="nav flex-column googleStyleUL">
                      {suggestions.map((suggestion: any) => (
                        <li
                          className="nav-item fs-12 googleStyleLI"
                          key={suggestion.place_id}
                          onClick={() =>
                            handleCitySelection(
                              suggestion.place_id,
                              suggestion.description
                            )
                          }
                        >
                          {suggestion.description}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {formik.touched.address && formik.errors.address && (
                    <div className="fv-plugins-message-container">
                      {/* <span role="alert">{formik.errors.address}</span> */}
                    </div>
                  )}
                </div>
                <div
                  className="d-flex align-items-center mt-2 cursor-pointer"
                  onClick={handleUseCurrentLocation}
                >
                  <img
                    src="/assets/images/locationicon.svg"
                    alt="Location Icon"
                    style={{
                      width: "20px",
                      height: "20px",
                      marginRight: "8px",
                    }}
                  />
                  <span className="text-primary fw-bold">
                    Use my current location
                  </span>
                </div>

                <div className="col-12 mt-3">
                  <div className="row">
                    <div className="col-4"></div>
                    <div className="col-8 text-end">
                      <button
                        className="btn btn-cancel rounded-5"
                        onClick={clickCancel}
                      >
                        Cancel
                      </button>
                      <button
                        disabled={formik.isSubmitting || !formik.isValid}
                        className="btn btn-outline-primary rounded-5 me-2"
                      >
                        {!loading && (
                          <span className="indicator-label">Publish</span>
                        )}
                        {loading && (
                          <span
                            className="indicator-progress"
                            style={{ display: "block" }}
                          >
                            Please wait...
                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </section>
          <Modal show={showModal} onHide={closeMap}>
            <Modal.Header closeButton>
              <Modal.Title>Select Your Location</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {!isLoaded ? (
                <div>Loading map...</div>
              ) : loadError ? (
                <div>Error loading maps</div>
              ) : (
                <div>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    zoom={17}
                    center={mapCenter}
                    onClick={handleMapClick}
                  >
                    {userLocation && (
                      <Marker
                        position={userLocation}
                        icon={{
                          url: "/assets/images/locationicon.svg",
                          scaledSize: new google.maps.Size(40, 40), // ✅ Corrected
                        }}
                        draggable={true}
                        onDragEnd={handleMarkerDragEnd}
                      />
                    )}
                  </GoogleMap>

                  <div className="d-flex gap-3 mt-2 justify-content-end">
                    <button
                      type="submit"
                      className="btn primary-btn px-3 py-1"
                      onClick={fetchAddress}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              )}
            </Modal.Body>
          </Modal>
        </div>
      </section>
    </>
  );
};
export { ProfileUpdate };
